import { Injectable, NgZone } from '@angular/core';
import { SensorData, LocationData } from '../../interfaces/certificate.interface';

interface AccelerometerData {
  x: number;
  y: number;
  z: number;
}

interface GyroscopeData {
  alpha: number | null;
  beta: number | null;
  gamma: number | null;
}

interface UserInteraction {
  action: string;
  timestamp: string;
  data?: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root',
})
export class SensorDataService {
  private scrollPattern: number[] = [];
  private pageLoadTime: Date | null = null;
  private tapVelocity: number | null = null;
  private lastAccelerometerEvent: AccelerometerData | null = null;
  private lastGyroscopeEvent: GyroscopeData | null = null;
  private userInteractions: UserInteraction[] = [];

  private deviceMotionHandler: ((event: DeviceMotionEvent) => void) | null =
    null;
  private deviceOrientationHandler:
    | ((event: DeviceOrientationEvent) => void)
    | null = null;

  constructor(private ngZone: NgZone) {}

  /**
   * Start tracking sensor data
   */
  startTracking(): void {
    this.pageLoadTime = new Date();
    this.userInteractions = [];

    // Device motion (accelerometer)
    if (typeof window !== 'undefined' && window.DeviceMotionEvent) {
      this.deviceMotionHandler = (event: DeviceMotionEvent) => {
        if (event.accelerationIncludingGravity) {
          this.lastAccelerometerEvent = {
            x: event.accelerationIncludingGravity.x || 0,
            y: event.accelerationIncludingGravity.y || 0,
            z: event.accelerationIncludingGravity.z || 0,
          };
        }
      };
      window.addEventListener('devicemotion', this.deviceMotionHandler);
    }

    // Device orientation (gyroscope)
    if (typeof window !== 'undefined' && window.DeviceOrientationEvent) {
      this.deviceOrientationHandler = (event: DeviceOrientationEvent) => {
        this.lastGyroscopeEvent = {
          alpha: event.alpha,
          beta: event.beta,
          gamma: event.gamma,
        };
      };
      window.addEventListener('deviceorientation', this.deviceOrientationHandler);
    }

    this.trackInteraction('tracking_started');
  }

  /**
   * Stop tracking sensor data
   */
  stopTracking(): void {
    if (this.deviceMotionHandler) {
      window.removeEventListener('devicemotion', this.deviceMotionHandler);
      this.deviceMotionHandler = null;
    }
    if (this.deviceOrientationHandler) {
      window.removeEventListener(
        'deviceorientation',
        this.deviceOrientationHandler,
      );
      this.deviceOrientationHandler = null;
    }
  }

  /**
   * Record scroll position
   */
  recordScroll(offset: number): void {
    this.scrollPattern.push(offset);
    if (this.scrollPattern.length > 10) {
      this.scrollPattern.shift();
    }
    this.trackInteraction('scroll', { offset });
  }

  /**
   * Record tap/click event
   */
  recordTap(): void {
    this.tapVelocity = Date.now();
    this.trackInteraction('tap');
  }

  /**
   * Track document view
   */
  recordDocumentView(): void {
    this.trackInteraction('document_viewed');
  }

  /**
   * Track consent button click
   */
  recordConsentClick(): void {
    this.trackInteraction('consent_clicked');
  }

  /**
   * Track checkbox interaction
   */
  recordCheckboxChange(checkboxId: string, checked: boolean): void {
    this.trackInteraction('checkbox_toggled', { checkboxId, checked });
  }

  /**
   * Collect all sensor data
   */
  collectSensorData(): Record<string, unknown> {
    const timeOnPage = this.pageLoadTime
      ? Date.now() - this.pageLoadTime.getTime()
      : 0;

    return {
      accuracy: this.calculateAccuracy(),
      accelerometer: this.lastAccelerometerEvent,
      gyroscope: this.lastGyroscopeEvent,
      tapVelocity: this.tapVelocity,
      scrollPattern:
        this.scrollPattern.length > 0 ? [...this.scrollPattern] : null,
      timeOnPageMs: timeOnPage,
      interactionCount: this.userInteractions.length,
      interactions: [...this.userInteractions],
    };
  }

  /**
   * Get sensor data in the format expected by the API
   */
  getSensorDataForPayload(location: LocationData | null): SensorData {
    const sensorData = this.collectSensorData();

    return {
      location: location,
      timestamp: new Date().toISOString(),
      accuracy: sensorData['accuracy'] as number | null,
    };
  }

  /**
   * Get all tracked user interactions (for audit trail)
   */
  getUserInteractions(): UserInteraction[] {
    return [...this.userInteractions];
  }

  /**
   * Reset all collected data
   */
  reset(): void {
    this.scrollPattern = [];
    this.pageLoadTime = null;
    this.lastAccelerometerEvent = null;
    this.lastGyroscopeEvent = null;
    this.tapVelocity = null;
    this.userInteractions = [];
  }

  /**
   * Dispose of resources
   */
  dispose(): void {
    this.stopTracking();
    this.reset();
  }

  /**
   * Track a user interaction
   */
  private trackInteraction(
    action: string,
    data?: Record<string, unknown>,
  ): void {
    this.userInteractions.push({
      action,
      timestamp: new Date().toISOString(),
      data,
    });
  }

  /**
   * Calculate accuracy score based on available sensor data
   */
  private calculateAccuracy(): number | null {
    const hasAccelerometer = this.lastAccelerometerEvent !== null ? 1.0 : 0.0;
    const hasGyroscope = this.lastGyroscopeEvent !== null ? 1.0 : 0.0;
    const hasScroll = this.scrollPattern.length > 0 ? 1.0 : 0.0;
    const hasTap = this.tapVelocity !== null ? 1.0 : 0.0;

    const totalSensors = 4.0;
    const activeSensors = hasAccelerometer + hasGyroscope + hasScroll + hasTap;

    if (activeSensors === 0) {
      return null;
    }

    return (activeSensors / totalSensors) * 10.0;
  }
}

